import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { TreePine, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const BG_IMG = "https://images.unsplash.com/photo-1733123306852-8b1e1be8272d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxzdGFja2VkJTIwdGltYmVyJTIwbG9nc3xlbnwwfHx8fDE3Nzg1Nzc0MTR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email.trim(), password);
      navigate(u?.must_change_password ? "/change-password" : "/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-10">
            <TreePine className="h-8 w-8 text-emerald-800" />
            <div>
              <div className="font-display text-2xl font-bold tracking-tight text-stone-950">Forestry WMS</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Warehouse · Orders · Finance</div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-stone-950 mb-2">
            Sign in to your workspace
          </h1>
          <p className="text-sm text-stone-500 mb-8">
            From log delivery to European export — all under one roof.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="data-label mb-1.5 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 text-sm font-mono"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="data-label mb-1.5 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 text-sm font-mono"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white py-2.5 rounded-sm font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </button>
          </form>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img src={BG_IMG} alt="Stacked timber" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-stone-950/50" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="data-label text-stone-300 mb-3">Built for export-grade operations</div>
            <h2 className="font-display text-3xl font-bold tracking-tight leading-tight">
              From the yard to the vessel — every pole accounted for.
            </h2>
            <p className="mt-4 text-sm text-stone-300">
              Live stock-on-hand, allocation locks, container-level profit, audit trail. The Excel replacement that ships.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
